import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import InfoIcon from "@material-ui/icons/InfoOutlined";
import {data} from "@tensorflow/tfjs";
import {handleError} from "../../App";
import {
	BOX_SHADOWS,
	DATA_TYPES,
	DROP_TYPES,
	EXTRA_HEADERS,
	FONTS,
	MIN_ROWS,
	SECONDARY_COLOR,
	WORKER_TYPES,
} from "../../constants";
import {getUsableColumns} from "../../dashboard/VariableCorrelations/DualChart";
import {
	FAILED_STATUS,
	SUCCESS_STATUS,
} from "../../MachineLearning/BackgroundTrainer";
import Column from "../../MachineLearning/Column";
import Dataset from "../../MachineLearning/Dataset";
import {getWorker} from "../../MachineLearning/getWorker";
import {PCAEncoder} from "../../MachineLearning/PCAReduction";
import check from "../../media/check.png";
import {cleanDataFilterType, stateType, stateTypeF} from "../../state";
import {Line} from "../Common/BoxHeader";
import DialogBoxWithButtons from "../Common/DialogBoxWithButtons";
import StyledDropzone from "../Common/DropZone";
import {sleep} from "../Common/helpers";
import {LogoNameMainPageScroll} from "../Common/LogoNameMainPageScroll";
import {resetDashboard} from "../Common/resetDashboard";
import {resetCleanData} from "../DataCleaning/CleanHeader";
import {makeCleanTableData} from "../DataCleaning/RowSelectTable";
import {MAX_TOPICS} from "../PersonaGroupings/CustomTopicTable";

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		dropZoneBoxContainer: {
			position: "relative",
			width: "100%",
			height: "100%",
		},
		dropZone: {
			position: "relative",
			margin: "30px",
			height: "78%",
		},
		alertInfo: {
			position: "relative",
			top: "-22px",
			left: "3%",
			width: "93%",
		},
		requirements: {
			display: "flex",
			flexDirection: "row",
			cursor: "pointer",
			transition: "color 0.2s",
			marginLeft: "30px",
			marginTop: "0px",
			marginBottom: "20px",
			height: "0px",
			color: SECONDARY_COLOR,
		},
		title: {
			_fontFamily: FONTS,
			get fontFamily() {
				return this._fontFamily;
			},
			set fontFamily(value) {
				this._fontFamily = value;
			},
			alignSelf: "center",
			fontSize: "30px",
			marginTop: "10px",
		},
		container: {
			boxShadow: BOX_SHADOWS,
			backgroundColor: "white",
			width: "100%",
			height: "370px",
			borderRadius: "10px",
		},
		dropContainer: {height: "370px", marginTop: "10px"},
	})
);

const AlertInfo = ({state}: stateType) => (
	<div>
		<b> {state.saveState.dataUpload.fileName}</b>
	</div>
);

export function calcPCA(state: stateTypeF, dataset: Dataset) {
	let mlData = dataset.getMLData(
		dataset.columns[0].columnName,
		undefined,
		undefined,
		false,
		true
	);

	const pcaEncoder = new PCAEncoder();
	pcaEncoder.train(mlData.X);
	mlData = pcaEncoder.encodeTrainSet(mlData);
	const maxData = Math.min(mlData.X.length, 10000);
	state.setPcaEncodedData(mlData.X.slice(0, maxData));
}

export function datasetFromJSON(data: string) {
	let dataset = Object.assign(new Dataset(undefined, undefined), data);

	for (let i = 0; i < dataset.columns.length; i++) {
		dataset.columns[i] = Object.assign(
			new Column(undefined, undefined),
			dataset.columns[i]
		);
	}
	return dataset as Dataset;
}

async function processDataInBackground(
	dataStr: string,
	shuffle: boolean,
	filters: cleanDataFilterType[],
	rowsToDrop: Set<number>,
	state: stateTypeF
) {
	const payload = {
		workerType: WORKER_TYPES.processDroppedData,
		dataStr: dataStr,
		shuffle: shuffle,
		filters: filters,
		rowsToDrop: rowsToDrop,
	};
	const worker = getWorker();

	let returnedData: string;
	let histData: number[][] = [];
	let rowsModified = 0;
	let rowsDropped = 0;
	let cors = undefined;
	let histDataType = DATA_TYPES.number;
	let valuesModified: Object = {};
	let autoFilters: cleanDataFilterType[] = undefined;

	let resolves = {};

	let workerStatus = FAILED_STATUS;
	worker.onmessage = function (message) {
		if (message.data?.status === SUCCESS_STATUS) {
			returnedData = message.data.dataset;
			histData = message.data.histData;
			if (filters === undefined) {
				filters = message.data.filters;
			}
			rowsModified = message.data.rowsModified;
			rowsDropped = message.data.rowsDropped;
			cors = message.data.cors;
			histDataType = message.data.histDataType;
			valuesModified = message.data.valuesModified;
			autoFilters = message.data.autoFilters;
			resolves[0]();
			delete resolves[0];
			workerStatus = SUCCESS_STATUS;
		} else {
			handleError(
				state,
				message.data?.error,
				WORKER_TYPES.postTrainProcess
			);
			resolves[0]();
			delete resolves[0];
		}
	};
	await new Promise(function (resolve, reject) {
		// save callbacks for later
		resolves[0] = resolve;
		worker.postMessage(payload);
	});
	if (workerStatus !== SUCCESS_STATUS) {
		return {
			undefined,
			histData,
			filters,
			rowsModified,
			rowsDropped,
			cors,
			histDataType,
			valuesModified,
			autoFilters,
		};
	}
	console.log("Returned");
	console.log(returnedData);
	const dataset = datasetFromJSON(returnedData);

	return {
		dataset,
		histData,
		filters,
		rowsModified,
		rowsDropped,
		cors,
		histDataType,
		valuesModified,
		autoFilters,
	};
}

export async function asyncApplyFiltersToDash(
	dataset: Dataset,
	filters: cleanDataFilterType[],
	rowsToDrop: Object,
	state: stateTypeF
) {
	let cors;
	let returnedColumns = dataset.columns;
	let rawFrequencies = dataset.columns.map((col) => col.rawFrequency);
	let valuesModified: Set<string> = new Set();
	const worker = getWorker();

	const payload = {
		workerType: WORKER_TYPES.applyFiltersToDash,
		columns: dataset.columns,
		data: dataset.data,
		filters: filters,
		rowsToDrop: rowsToDrop,
	};

	let resolves = {};
	let workerStatus = FAILED_STATUS;
	worker.onmessage = function (message) {
		if (message.data?.status === SUCCESS_STATUS) {
			returnedColumns = message.data.columns;
			rawFrequencies = JSON.parse(message.data.rawFrequencies);
			cors = message.data.cors;
			valuesModified = message.data.valuesModified;
			resolves[0]();
			delete resolves[0];
			workerStatus = SUCCESS_STATUS;
		} else {
			handleError(
				state,
				message.data?.error,
				WORKER_TYPES.applyFiltersToDash
			);
			resolves[0]();
			delete resolves[0];
		}
	};
	await new Promise(function (resolve, reject) {
		// save callbacks for later
		resolves[0] = resolve;
		worker.postMessage(payload);
	});
	if (workerStatus !== SUCCESS_STATUS) {
		return {
			returnedColumns: undefined,
			cors: undefined,
			valuesModified: undefined,
		};
	}

	for (let i = 0; i < returnedColumns.length; i++) {
		returnedColumns[i].rawFrequency = rawFrequencies[i];
	}

	let colNames = dataset.columns.map((c) => c.columnName);
	function colSorter(a: Column, b: Column) {
		let indexA = colNames.indexOf(a.columnName);
		let indexB = colNames.indexOf(b.columnName);
		if (indexA < indexB) {
			return -1;
		}
		if (indexA > indexB) {
			return 1;
		}
		return 0;
	}
	returnedColumns = returnedColumns.sort(colSorter);
	return {returnedColumns, cors, valuesModified};
}

export async function processDroppedData(
	state: stateTypeF,
	dataStr: string,
	loadingText: string,
	reCalcCleanFilterData = true,
	rowsToDrop: Set<number> = new Set(),
	inFilters?: cleanDataFilterType[],
	dropType: number = DROP_TYPES.main
) {
	if (state.isFirstLoad) {
		state.setIsNewDashPopUpOpen(false);
		state.setIsLoadingPopupOpen(true);
	}
	await sleep(200);

	let t0 = performance.now();

	let {
		dataset,
		histData,
		filters,
		rowsModified,
		rowsDropped,
		cors,
		histDataType,
		valuesModified,
		autoFilters,
	} = await processDataInBackground(
		dataStr,
		true,
		inFilters,
		rowsToDrop,
		state
	);

	if (dataset === undefined) {
		return;
	}
	if (dataset.nRows < 50) {
		state.setRejectedReason(
			"Your Dataset must have at least 50 usable rows."
		);
		state.setIsLoadingPopupOpen(false);
		state.setIsRejectedDropOpen(true);
		return;
	}

	const cols = getUsableColumns(dataset);

	const usable = dataset.columns.filter(
		(col) => col.columnType !== DATA_TYPES.unusable
	);
	if (usable.length < 2) {
		state.setRejectedReason(
			"Your Dataset must have at least 2 columns suitable for machine learning."
		);
		state.setIsLoadingPopupOpen(false);
		state.setIsRejectedDropOpen(true);
		return;
	}
	state.saveState.readOnly = false;

	state.saveState.topicModeling.varSelected = 0;
	state.saveState.sentiment.varSelected = 0;

	if (dataset.visualizationData !== undefined) {
		state.setTagClicked(
			dataset.visualizationData[0].topicCloudData.cloudData[0].value
		);
	}
	state.saveState.dataUpload.histTabSelected =
		histDataType === DATA_TYPES.number ? 0 : 1;
	state.saveState.dataUpload.dataIngestionHistData = [...histData];
	state.saveState.cleanData.cleanHistData = undefined;

	state.saveState.correlations.correlations = cors;
	state.saveState.correlations.dualChartSelected = [
		cols.xCol.columnName,
		cols.yCol.columnName,
	];
	state.saveState.cleanData.valuesModified = valuesModified;
	state.setSaveState(state.saveState);

	state.resetAddChartInputs();
	state.setAddedCharts([]);
	if (autoFilters !== undefined) {
		state.saveState.cleanData.autoFilters = autoFilters;
		state.setSaveState(state.saveState);
	}

	let colv = undefined;
	for (let col of dataset.columns) {
		if (
			[DATA_TYPES.categorical, DATA_TYPES.number].includes(col.columnType)
		) {
			colv = col;
			break;
		}
	}
	if (reCalcCleanFilterData) {
		resetCleanData(state, dataset, colv.columnName);
		state.saveState.cleanData.cleanHistSelected = colv.columnName;
		state.setSaveState(state.saveState);
	}

	state.saveState.cleanData.cleanDataFilters = filters;
	state.saveState.cleanData.cleanDataFiltersApplied = [...filters];
	state.setSaveStateModified(true);
	state.setSaveState(state.saveState);
	state.setFilterSummary({rowsModified, rowsDropped});

	if (dropType === DROP_TYPES.newDashPopup) {
		state.setNewDashName();
	}

	setDashboard(state, dataset, loadingText);
	await sleep(500);
	state.setIsLoadingPopupOpen(false);

	if (reCalcCleanFilterData) {
		makeCleanData(state, dataset);
	}
	state.setFileName(localStorage.getItem("tempFileName"), state.saveState);
	state.setReCalcSentTable(true);
	//Reset Topics Data
	state.setAllEnteredPhrases(Array(MAX_TOPICS).fill(""));
	state.setAllCheckedTopics(Array(MAX_TOPICS).fill(true));

	let t1 = performance.now();
	let toSleep = 3000 - (t1 - t0);
	if (toSleep > 0) {
		await sleep(toSleep);
	}
	state.setIsFirstLoad(true);
	state.setIsLoadingPopupOpen(false);
}

export function makeCleanData(state, dataset: Dataset) {
	let pageData = makeCleanTableData(dataset);

	state.setCleanFilteredData(pageData);
	state.setCleanData({
		data: pageData,
		headers: [
			EXTRA_HEADERS.row_number,
			...dataset.columns.map((col) => col.columnName),
		],
	});
}

export async function setDashboard(
	state: stateTypeF,
	dataset: Dataset,
	loadingText?: string
) {
	if (loadingText !== undefined) {
		state.setDialogText(loadingText);
		state.setIsDialogOpen(true);
		await sleep(200);
	}

	state.resetPredictorValues();

	if (dataset.nRows < MIN_ROWS) {
		state.setDialogText("Data Must Have at Least " + MIN_ROWS + " Rows.");
		return;
	}

	//Check if enough columns are usable
	const usableCols = dataset.columns.filter(
		(col) => col.columnType != DATA_TYPES.unusable
	);
	const anyUsabe = usableCols.length >= 2;

	if (!anyUsabe) {
		state.setDialogText(
			"File does not have usable data for Machine Learning."
		);
		return;
	}

	state.saveState.dataset = dataset;
	state.setSaveState(state.saveState);
	state.setColNames(dataset.columns.map((col) => col.columnName));

	//Reset some state values
	resetDashboard(state);

	//Set the default value to predict
	let valToPredict = "";
	for (let col of dataset.columns) {
		if (
			col.columnType === DATA_TYPES.unusable ||
			(col.columnType === DATA_TYPES.categorical &&
				col.nonSparseClasses.length <= 1)
		) {
			continue;
		}
		valToPredict = col.columnName;
		if (col.columnType === DATA_TYPES.categorical) {
			state.saveState.variableImportance.impactClassSelected =
				dataset.getColumn(valToPredict).nonSparseClasses[0];
			state.setSaveState(state.saveState);
		}

		state.saveState.modelTraining.valueToPredict = col.columnName;
		state.setSaveState(state.saveState);
		state.setCurrentModels(col.columnType);
		break;
	}
	//Set the default numeric hist table columns
	for (let col of dataset.columns) {
		if (col.columnType === DATA_TYPES.number) {
			state.saveState.dataUpload.numericHistSelected = col.columnName;
			state.setSaveState(state.saveState);
			break;
		}
	}
	for (let col of dataset.columns) {
		if (col.columnType === DATA_TYPES.categorical) {
			state.saveState.dataUpload.catHistSelected = col.columnName;
			state.setSaveState(state.saveState);
			break;
		}
	}

	await sleep(200);
	state.setIsDialogOpen(false);
}

export function checkLoggedIn(state: stateTypeF) {
	let valstr = localStorage.getItem("profile");
	if (valstr !== null) {
		state.setIsLoggedIn(true);
		let val = JSON.parse(valstr);
		state.setClientName(val["givenName"]);
		state.setAvatarURL(val["imageUrl"]);
		state.setClientID(val["client_id"]);
		return val;
	} else {
		return undefined;
	}
}

export const DropZoneBox = ({state}: stateType) => {
	const classes = useStyles();

	function callbackFunction(dataStr: string) {
		const anySavedModels = state.saveState.savedModels.length > 0;
		if (anySavedModels && !state.isSaved) {
			state.setTempDataStr(dataStr);
			state.setIsResetDialogOpen(true);
		} else {
			processDroppedData(state, dataStr, "Loading Data...");
		}
	}

	function continueButton() {
		processDroppedData(state, state.getTempDataStr(), "Loading Data...");
		state.setTempDataStr("");
		state.setIsResetDialogOpen(false);
	}
	function cancelFunction() {
		state.setTempDataStr("");
		state.setIsResetDialogOpen(false);
	}

	function openTips() {
		state.setRequirementsOpen(true);
	}
	function closeTips() {
		state.setRequirementsOpen(false);
	}

	return (
		<div className={classes.dropZoneBoxContainer}>
			<div className={classes.requirements} onClick={openTips}>
				<InfoIcon
					style={{paddingTop: "0px", height: "20px"}}
				></InfoIcon>
				<div style={{marginTop: "0px", marginLeft: "5px"}}>
					See Data Requirements Checklist Here.
				</div>
			</div>
			<div className={classes.dropZone}>
				<StyledDropzone
					state={state}
					callbackFunction={(val) => callbackFunction(val)}
					saveOpen={true}
				></StyledDropzone>
			</div>
			<div className={classes.alertInfo}>
				<AlertInfo state={state}></AlertInfo>
			</div>
			<DialogBoxWithButtons
				state={state}
				isOpen={state.isResetDialogOpen}
				hintText={
					"Uploading a new dataset will reset the Dashboard. All unsaved models will be discarded."
				}
				handleCancel={cancelFunction}
				handleContinue={continueButton}
			></DialogBoxWithButtons>
			<Dialog
				open={state.requirementsOpen}
				onClose={closeTips}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
				style={{alignItems: "center", width: "100vw"}}
			>
				<DialogContent
					style={{
						marginBottom: "20px",
						marginLeft: "-15px",
						width: "320px",
						overflow: "hidden",
					}}
				>
					<div style={{marginTop: "-30px"}}>
						<div
							style={{
								marginLeft: "40px",
								marginBottom: "-35px",
							}}
						>
							<LogoNameMainPageScroll></LogoNameMainPageScroll>
						</div>
						<div
							style={{
								marginLeft: "40px",
								marginBottom: "14px",
								marginTop: "30px",
								fontFamily: FONTS,
								fontSize: "18px",
							}}
						>
							Data Requirements Checklist
						</div>
						<Line></Line>
						<div style={{marginLeft: "10px", marginTop: "10px"}}>
							<CheckItem
								name={"File is in CSV Format"}
							></CheckItem>
							<CheckItem
								name={"File is Less Than 25MB"}
							></CheckItem>
							<CheckItem
								name={"File has Column Names"}
							></CheckItem>
							<CheckItem
								name={"File has at Least 3 Usable Columns"}
							></CheckItem>
							<CheckItem
								name={"File has at Least 50 Rows"}
							></CheckItem>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export function CheckItem({name}) {
	return (
		<div style={{display: "flex"}}>
			<img src={check} style={{width: "30px"}}></img>
			<div
				style={{
					marginTop: "6px",
					fontFamily: FONTS,
					fontSize: "17px",
					marginLeft: "5px",
				}}
			>
				{name}
			</div>
		</div>
	);
}

export default DropZoneBox;

export function DropZoneBoxx({state}: stateType) {
	let classes = useStyles();
	return (
		<div className={classes.container}>
			<div className={classes.dropContainer}>
				<DropZoneBox state={state}></DropZoneBox>
			</div>
		</div>
	);
}
